export interface MetricDataModel {
    text: string;
    color: string;
    iconUrl?: string;
}

export enum MetricTimeFrame {
    DAILY = 'daily',
    WEEKLY = 'weekly',
    MONTHLY = 'monthly',
}
export enum METRIC_SUCCESS_MESSAGE_THRESHOLD {
    NOTIFICATION_OPENED_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE = 1,
    APP_VISITED_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE = 10,
    ADDED_TO_CART_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE = 2,
    REVENUE_GENERATED_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE = 0.5,
    COMPOSITE_SUCCESS_RATE = 5,
    FRESHNESS_SCORE = 70,
    EMAIL_DELIVERY_PERCENTAGE = 75, //temporary numbers
    EMAIL_UNSUBSCRIBE_PERCENTAGE = 20,
    EMAIL_OPEN_PERCENTAGE = 75,
}

export enum METRIC_NAMES {
    FRESHNESS_SCORE = 'message_freshness_score',
    ACTIVE_USERS = 'active_users',
    COMPOSITE_SUCCESS_RATE = 'composite_success_rate',
    MESSAGING_FREQUENCY = 'messaging_frequency',
    // num messages
    MESSAGES_SENT_PER_TIME_INTERVAL = 'num_messages_sent_per_time_interval',
    NUM_MESSAGES_SENT_COUNT = 'num_messages_sent_count',
    NUM_MESSAGES_SENT_PERCENTAGE = 'num_messages_sent_percentage',
    // add to cart
    ADDED_TO_CART_ = 'added_to_cart_',
    ADDED_TO_CART_EVENTS_COUNT = 'added_to_cart_events_count',
    ADDED_TO_CART_EVENTS_PERCENTAGE = 'added_to_cart_events_percentage',
    ADDED_TO_CART_IMPACT_AVERAGE_COUNT = 'added_to_cart_impact_average_count',
    ADDED_TO_CART_IMPACT_COUNT = 'added_to_cart_impact_count',
    ADDED_TO_CART_IMPACT_PERCENT = 'added_to_cart_impact_percentage',
    ADDED_TO_CART_SUCCESS_COUNT = 'added_to_cart_success_count',
    ADDED_TO_CART_SUCCESS_RATE = 'added_to_cart_success_rate',
    ADDED_TO_CART_SUCCESSFUL_MESSAGES_SENT_COUNT = 'added_to_cart_successful_messages_sent_count',
    ADDED_TO_CART_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE = 'added_to_cart_successful_messages_sent_percentage',
    // app visited
    APP_VISITED_ = 'app_visited_',
    APP_VISITED_EVENTS_COUNT = 'app_visited_events_count',
    APP_VISITED_EVENTS_PERCENTAGE = 'app_visited_events_percentage',
    APP_VISITED_IMPACT_AVERAGE_COUNT = 'app_visited_impact_average_count',
    APP_VISITED_IMPACT_COUNT = 'app_visited_impact_count',
    APP_VISITED_IMPACT_PERCENT = 'app_visited_impact_percentage',
    APP_VISITED_SUCCESS_COUNT = 'app_visited_success_count',
    APP_VISITED_SUCCESS_RATE = 'app_visited_success_rate',
    APP_VISITED_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE = 'app_visited_successful_messages_sent_percentage',
    APP_VISITED_SUCCESSFUL_MESSAGES_SENT_COUNT = 'app_visited_successful_messages_sent_count',
    // notification opened
    NOTIFICATION_OPENED_ = 'notification_opened_',
    NOTIFICATION_OPENED_EVENTS_COUNT = 'notification_opened_events_count',
    NOTIFICATION_OPENED_EVENTS_PERCENTAGE = 'notification_opened_events_percentage',
    NOTIFICATION_OPENED_SUCCESS_COUNT = 'notification_opened_success_count',
    NOTIFICATION_OPENED_SUCCESS_RATE = 'notification_opened_success_rate',
    NOTIFICATION_OPENED_SUCCESSFUL_MESSAGES_SENT_COUNT = 'notification_opened_successful_messages_sent_count',
    NOTIFICATION_OPENED_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE = 'notification_opened_successful_messages_sent_percentage',
    // revenue
    REVENUE_GENERATED_ = 'revenue_generated_',
    REVENUE_GENERATED_EVENTS_COUNT = 'revenue_generated_events_count',
    REVENUE_GENERATED_EVENTS_PERCENTAGE = 'revenue_generated_events_percentage',
    REVENUE_GENERATED_IMPACT_AVERAGE_COUNT = 'revenue_generated_impact_average_count',
    REVENUE_GENERATED_IMPACT_COUNT = 'revenue_generated_impact_count',
    REVENUE_GENERATED_IMPACT_PERCENT = 'revenue_generated_impact_percentage',
    REVENUE_GENERATED_SUCCESS_COUNT = 'revenue_generated_success_count',
    REVENUE_GENERATED_SUCCESS_RATE = 'revenue_generated_success_rate',
    REVENUE_GENERATED_SUCCESSFUL_MESSAGES_SENT_COUNT = 'revenue_generated_successful_messages_sent_count',
    REVENUE_GENERATED_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE = 'revenue_generated_successful_messages_sent_percentage',
    //email specific
    EMAIL_OPEN_COUNT = 'email_open_successful_messages_sent_count',
    EMAIL_OPEN_PERCENTAGE = 'email_open_successful_messages_sent_percentage',
    EMAIL_CLICK_COUNT = 'email_click_successful_messages_sent_count',
    EMAIL_CLICK_PERCENTAGE = 'email_click_successful_messages_sent_percentage',
    EMAIL_UNSUBSCRIBE_COUNT = 'email_unsubscribe_successful_messages_sent_count',
    EMAIL_UNSUBSCRIBE_PERCENTAGE = 'email_unsubscribe_successful_messages_sent_percentage',
    EMAIL_BOUNCE_COUNT = 'email_bounce_successful_messages_sent_count',
    EMAIL_BOUNCE_PERCENTAGE = 'email_bounce_successful_messages_sent_percentage',

    // surface specific count
    CONTENT_CONSUMED_COUNT = 'content_consumed_count',
    CONTENT_MISSED_COUNT = 'content_missed_count',
    CONTENT_SHOWN_COUNT = 'content_shown_count',
    CONTENT_CLICKED_COUNT = 'content_clicked_count',
    CONTENT_CONVERTED_COUNT = 'content_converted_count',
    // surface specific rate
    CONTENT_SHOWN_PERCENTAGE = 'content_shown_percentage',
    CONTENT_CLICKED_PERCENTAGE = 'content_clicked_percentage',
    CONTENT_CONVERTED_PERCENTAGE = 'content_converted_percentage',
}

export const successMetrics = [
    METRIC_NAMES.NOTIFICATION_OPENED_SUCCESS_RATE,
    METRIC_NAMES.APP_VISITED_SUCCESS_RATE,
    METRIC_NAMES.ADDED_TO_CART_SUCCESS_RATE,
    METRIC_NAMES.REVENUE_GENERATED_SUCCESS_RATE,
] as const;
export type SUCCESS_METRICS = typeof successMetrics[number];

export const performanceMetricsCount = [
    METRIC_NAMES.NOTIFICATION_OPENED_SUCCESS_COUNT,
    METRIC_NAMES.APP_VISITED_SUCCESS_COUNT,
    METRIC_NAMES.ADDED_TO_CART_SUCCESS_COUNT,
    METRIC_NAMES.REVENUE_GENERATED_SUCCESS_COUNT,
] as const;
export type PERFORMANCE_METRICS_COUNT = typeof performanceMetricsCount[number];

export const successAndPerformanceMetricsWithCompositeMetric = [
    ...successMetrics,
    ...performanceMetricsCount,
    METRIC_NAMES.COMPOSITE_SUCCESS_RATE,
] as const;
export type SUCCESS_AND_PERFORMANCE_METRICS_WITH_COMPOSITE_METRIC =
    typeof successAndPerformanceMetricsWithCompositeMetric[number];

export enum ControlGroup {
    VERY_HIGH = 'very_high',
    HIGH = 'high',
    STILL_EXPLORING = 'still_exploring',
    CONTROL = 'control',
    LOW = 'low',
    VERY_LOW = 'very_low',
}

export interface MetricInfoModel {
    display_name: string;
    description?: string;
    iconUrl?: string;
}

export interface TopFormulasMetricModel {
    formula_id: number;
    formula_name: string;
    count?: number;
    percent?: number;
}
export interface TopLabelsMetricModel {
    label_id: number;
    label_name: string;
    count?: number;
    percent?: number;
    variant_type: string;
}
export interface TopTriggersMetricModel {
    compound_trigger_id: string;
    compound_trigger_name: string;
    count?: number;
    percent?: number;
}

export const topPerformingMetricsCount = [
    METRIC_NAMES.NOTIFICATION_OPENED_SUCCESSFUL_MESSAGES_SENT_COUNT,
    METRIC_NAMES.APP_VISITED_SUCCESSFUL_MESSAGES_SENT_COUNT,
    METRIC_NAMES.ADDED_TO_CART_SUCCESSFUL_MESSAGES_SENT_COUNT,
    METRIC_NAMES.REVENUE_GENERATED_SUCCESSFUL_MESSAGES_SENT_COUNT,
] as const;

export const topPerformingMetricsPercent = [
    METRIC_NAMES.NOTIFICATION_OPENED_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE,
    METRIC_NAMES.APP_VISITED_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE,
    METRIC_NAMES.ADDED_TO_CART_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE,
    METRIC_NAMES.REVENUE_GENERATED_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE,
] as const;

const topPerformingMetrics = [
    ...topPerformingMetricsCount,
    ...topPerformingMetricsPercent,
] as const;

export type TOP_PERFORMING_METRICS = typeof topPerformingMetrics[number];

export const TOP_PERFORMING_METRICS_DROPDOWN = [
    METRIC_NAMES.ADDED_TO_CART_,
    METRIC_NAMES.APP_VISITED_,
    METRIC_NAMES.REVENUE_GENERATED_,
    METRIC_NAMES.NOTIFICATION_OPENED_,
];

export enum TOP_METRICS_CARDS_VIEW_BY {
    PERCENTAGE = 'percentage',
    COUNT = 'count',
}

export enum TOP_METRICS_VIEW_BY {
    PERCENTAGE = 'percent',
    COUNT = 'avg_14_day',
}

export const dailyImpactMetricsCount = [
    METRIC_NAMES.ADDED_TO_CART_IMPACT_COUNT,
    METRIC_NAMES.APP_VISITED_IMPACT_COUNT,
    METRIC_NAMES.REVENUE_GENERATED_IMPACT_COUNT,
] as const;

export const dailyImpactMetricsPercent = [
    METRIC_NAMES.ADDED_TO_CART_IMPACT_PERCENT,
    METRIC_NAMES.APP_VISITED_IMPACT_PERCENT,
    METRIC_NAMES.REVENUE_GENERATED_IMPACT_PERCENT,
] as const;

export const dailyImpactMetrics = [
    ...dailyImpactMetricsCount,
    ...dailyImpactMetricsPercent,
] as const;

export type DAILY_IMPACT_METRICS = typeof dailyImpactMetrics[number];

export type DailyImpactMetricBaseModel = Partial<
    Record<DAILY_IMPACT_METRICS | 'metric_date', string | number>
>;

export type DailyImpactMetricsModel = Record<
    string,
    DailyImpactMetricBaseModel
>;

export const topPerformanceDisplayNameMapping: Record<
    DAILY_IMPACT_METRICS,
    METRIC_NAMES
> = {
    [METRIC_NAMES.ADDED_TO_CART_IMPACT_COUNT]: METRIC_NAMES.ADDED_TO_CART_,
    [METRIC_NAMES.APP_VISITED_IMPACT_COUNT]: METRIC_NAMES.APP_VISITED_,
    [METRIC_NAMES.REVENUE_GENERATED_IMPACT_COUNT]:
        METRIC_NAMES.REVENUE_GENERATED_,
    [METRIC_NAMES.ADDED_TO_CART_IMPACT_PERCENT]: METRIC_NAMES.ADDED_TO_CART_,
    [METRIC_NAMES.APP_VISITED_IMPACT_PERCENT]: METRIC_NAMES.APP_VISITED_,
    [METRIC_NAMES.REVENUE_GENERATED_IMPACT_PERCENT]:
        METRIC_NAMES.REVENUE_GENERATED_,
};
export interface TopStatsModel {
    active_messages: number;
    unique_active_users: number;
    messages_scheduled_today: number;
}
export interface MetricStatsApiModel {
    stats: TopStatsModel;
}

export const performanceMetrics = [
    ...topPerformingMetrics,
    METRIC_NAMES.COMPOSITE_SUCCESS_RATE,
    METRIC_NAMES.FRESHNESS_SCORE,
    METRIC_NAMES.NUM_MESSAGES_SENT_COUNT,
    METRIC_NAMES.NUM_MESSAGES_SENT_PERCENTAGE,
    METRIC_NAMES.EMAIL_OPEN_COUNT,
    METRIC_NAMES.EMAIL_OPEN_PERCENTAGE,
    METRIC_NAMES.EMAIL_UNSUBSCRIBE_COUNT,
    METRIC_NAMES.EMAIL_UNSUBSCRIBE_PERCENTAGE,
    METRIC_NAMES.EMAIL_BOUNCE_PERCENTAGE,
    METRIC_NAMES.EMAIL_CLICK_COUNT,
    METRIC_NAMES.EMAIL_CLICK_PERCENTAGE,
] as const;

export type PERFORMANCE_METRICS = typeof performanceMetrics[number];

export type PerformanceMetricsModel = {
    [key in PERFORMANCE_METRICS]: string;
};

export type PerformanceMetricsRawModel = {
    [key in PERFORMANCE_METRICS]: Record<string, number>;
};

export type PerformanceMetricsDataModel = {
    [key in PERFORMANCE_METRICS]: MetricDataModel;
};

export const successAndTopPerformanceMetricsWithCompositeMetric = [
    ...topPerformingMetrics,
    METRIC_NAMES.COMPOSITE_SUCCESS_RATE,
] as const;

export enum MetricsNDays {
    LAST_WEEK = 7,
    LAST_2_WEEK = 14,
}

export enum PerformanceMetricSlug {
    SUCCESSFUL_MESSAGES_SENT_COUNT = 'successful_messages_sent_count',
    SUCCESSFUL_MESSAGES_SENT_PERCENTAGE = 'successful_messages_sent_percentage',
}

export const METRICS_RETENTIONS_DAYS = 365;
